// Actionable recommendations engine with educational context and sources

export function generateRecommendations(data) {
  const { recipes, ingredients, breakEvenData, pnlData } = data;
  const tips = [];

  // Analyze recipes
  if (recipes.length > 0) {
    const avgFoodCost = recipes.reduce((s, r) => s + (r.foodCostPercent || 0), 0) / recipes.length;
    const avgPrimeCost = recipes.reduce((s, r) => s + (r.primeCostPercent || 0), 0) / recipes.length;
    const worstRecipe = [...recipes].sort((a, b) => (b.foodCostPercent || 0) - (a.foodCostPercent || 0))[0];
    const bestRecipe = [...recipes].sort((a, b) => (b.grossProfit || 0) - (a.grossProfit || 0))[0];

    // Food cost analysis
    if (avgFoodCost > 35) {
      tips.push({
        type: 'critical',
        title: 'Your food cost is too high',
        why: 'Food cost is the percentage of your menu price that goes to ingredients. The National Restaurant Association says the average for full-service restaurants is 28-35%. For pizza specifically, you should target 25-32% because dough and sauce are low-cost, high-margin items.',
        detail: `Your average food cost is ${avgFoodCost.toFixed(1)}%. That means for every $1 a customer pays, you're spending over $0.${Math.round(avgFoodCost)} just on ingredients — before labor, rent, or anything else.`,
        actions: [
          'Review portion sizes — even a 10% reduction per item can drop food cost by 2-3 points without customers noticing',
          'Get quotes from 2-3 suppliers for your top 5 ingredients. Even saving $0.50/lb on cheese adds up fast at volume',
          'Cross-use ingredients across recipes to reduce waste and increase buying power',
        ],
        source: 'National Restaurant Association, 2024 State of the Industry Report',
        learnMore: 'https://restaurant.org/research-and-media/research/research-reports/national-statistics/',
      });
    } else if (avgFoodCost > 30) {
      tips.push({
        type: 'warning',
        title: 'Food cost is above the sweet spot',
        why: 'Food cost tells you how efficiently you turn ingredients into revenue. The "sweet spot" for most pizza operations is 25-30%. Above that, your margins start getting squeezed by rent and labor.',
        detail: `At ${avgFoodCost.toFixed(1)}%, you're workable but there's room. Every 1% drop in food cost goes straight to your bottom line. On $50K/month revenue, that's $500/month more profit.`,
        actions: [
          'Audit your top 3 highest-cost recipes for substitution opportunities',
          'Implement portioning tools (scales, ladles, scoops) — over-portioning is the #1 silent profit killer',
          'Consider "menu engineering": promote high-margin items more visibly',
        ],
        source: 'Toast Restaurant Management Guide',
        learnMore: 'https://pos.toasttab.com/blog/on-the-line/food-cost-percentage',
      });
    } else {
      tips.push({
        type: 'success',
        title: 'Food cost is on point',
        why: 'Food cost percentage = (ingredient cost / menu price) x 100. Keeping this under 30% for a pizza shop means you have healthy margin to cover labor, overhead, and still pocket profit.',
        detail: `${avgFoodCost.toFixed(1)}% is solid. You're running a tight ship on ingredients. The industry average is 28-32%, so you're beating it.`,
        actions: [
          'Keep monitoring — supplier prices change seasonally. Check your costs monthly.',
          'Lock in pricing with your top vendors when prices dip',
        ],
        source: 'Restaurant365 Industry Benchmarks',
        learnMore: 'https://www.restaurant365.com/blog/how-to-calculate-food-cost-percentage/',
      });
    }

    // Prime cost
    if (avgPrimeCost > 65) {
      tips.push({
        type: 'critical',
        title: 'Prime cost is eating your profit',
        why: 'Prime cost = Food Cost + Labor Cost. It\'s the single most important number in your restaurant. Industry standard says it should be under 60%. The reason? After prime cost, you still have rent, utilities, insurance, and everything else. If prime cost is 65%+, there\'s almost nothing left.',
        detail: `Your prime cost is ${avgPrimeCost.toFixed(1)}%. That means for every dollar of revenue, $0.${Math.round(avgPrimeCost)} goes to food and labor. Only $0.${Math.round(100 - avgPrimeCost)} is left for everything else — and profit.`,
        actions: [
          'Cross-train staff so fewer people handle more tasks during slow periods',
          'Batch-prep sauces and pre-portion toppings at shift start — this cuts active labor time per order',
          'Consider a modest menu price increase (8-12%) on your 5 most popular items',
        ],
        source: 'National Restaurant Consultants, Prime Cost Guidelines',
        learnMore: 'https://www.restaurantowner.com/public/Prime-Cost-The-Most-Important-Number.cfm',
      });
    } else if (avgPrimeCost > 55) {
      tips.push({
        type: 'success',
        title: 'Prime cost is well-managed',
        why: 'Prime cost (food + labor) is the #1 metric that separates profitable restaurants from struggling ones. Under 60% means you have enough margin to cover overhead and still profit. You\'re there.',
        detail: `${avgPrimeCost.toFixed(1)}% prime cost is in the healthy zone (under 60%). Nice work balancing food cost and labor.`,
        actions: [
          'Maintain this ratio as you grow — don\'t let labor creep up during slow seasons',
          'Watch for "scope creep" — adding menu items that require more prep time can silently raise prime cost',
        ],
        source: 'Restaurant Owner Financial Management',
        learnMore: 'https://www.restaurantowner.com/public/Prime-Cost-The-Most-Important-Number.cfm',
      });
    }

    // Worst recipe
    if (worstRecipe && worstRecipe.foodCostPercent > 35) {
      tips.push({
        type: 'warning',
        title: `"${worstRecipe.name}" needs attention`,
        why: 'Every menu item doesn\'t need to hit 25% food cost — but if one item is way above average, it drags your whole menu down. Think of your menu as a team: some items carry the margin, others eat it.',
        detail: `At ${(worstRecipe.foodCostPercent || 0).toFixed(1)}% food cost, "${worstRecipe.name}" is your most expensive item to make relative to what you charge. Every time someone orders it instead of a higher-margin item, you lose ground.`,
        actions: [
          `Consider raising the price from $${(worstRecipe.menuPrice || 0).toFixed(2)} to $${((worstRecipe.ingredientCost || 0) / 0.28).toFixed(2)} (targeting 28% food cost)`,
          'Or reduce the portion size slightly — customers rarely notice a 5-10% reduction',
          'If it\'s a signature item, keep it but pair it with high-margin sides and drinks',
        ],
        source: 'Menu Engineering principles (Smith & Kasavana, Cornell University)',
        learnMore: 'https://ecommons.cornell.edu/items/3aee2af2-20a0-43c2-9adf-15f6aed80c40',
      });
    }

    // Best recipe
    if (bestRecipe && bestRecipe.grossProfit > 0) {
      tips.push({
        type: 'success',
        title: `"${bestRecipe.name}" is your profit star`,
        why: 'In menu engineering, this is called a "Star" — high popularity AND high profit. These are the items you want every customer ordering. They\'re your money-makers.',
        detail: `$${(bestRecipe.grossProfit || 0).toFixed(2)} gross profit per unit. This is the item you should be pushing harder than anything else.`,
        actions: [
          'Put it first on the menu or in a highlighted box — studies show the first item in a section gets 15-20% more orders',
          'Train staff to suggest it: "Our most popular is the [item] — it\'s what I\'d get"',
          'Create combo deals around this item to drive volume',
        ],
        source: 'Menu Engineering: Stars, Plowhorses, Puzzles & Dogs (Kasavana & Smith)',
        learnMore: 'https://pos.toasttab.com/blog/on-the-line/menu-engineering',
      });
    }
  }

  // Break-even
  const totalFixed = ['rent', 'utilities', 'insurance', 'software', 'payroll', 'managerSalary', 'loanPayments', 'otherFixed']
    .reduce((s, k) => s + (Number(breakEvenData[k]) || 0), 0);
  const margin = (Number(breakEvenData.avgContributionMargin) || 1) / 100;
  const avgOrder = Number(breakEvenData.avgOrderValue) || 1;

  if (totalFixed > 0) {
    const beRevenue = margin > 0 ? totalFixed / margin : 0;
    const beDaily = beRevenue / 30;
    const beOrders = beDaily / avgOrder;

    tips.push({
      type: beOrders > 80 ? 'warning' : 'info',
      title: `You need ${Math.ceil(beOrders)} orders/day to break even`,
      why: 'Break-even is the point where revenue covers all your costs — fixed (rent, insurance) plus variable (food, labor). Below break-even, you\'re losing money every day you\'re open. Above it, you\'re making profit. Knowing this number changes how you plan every shift.',
      detail: `That's $${beDaily.toFixed(0)}/day or $${beRevenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}/month. Everything above this line is actual profit you get to keep.`,
      actions: [
        beOrders > 60 ? 'This is a high number. Focus on reducing fixed costs OR increasing average order value — both move the needle.' : 'This is achievable. Focus on consistent daily volume and avoid slow-day slumps.',
        'Increase average ticket: add drink/side combos ($3-5 more per order adds up to thousands/month)',
        'Reduce fixed costs: renegotiate your lease, shop insurance annually, cut software you don\'t use',
      ],
      source: 'SBA Small Business Financial Guide',
      learnMore: 'https://www.sba.gov/business-guide/plan-your-business/calculate-your-startup-costs',
    });
  }

  // P&L
  const revenue = Number(pnlData.revenue) || 0;
  const totalExpenses = ['cogs', 'labor', 'rent', 'utilities', 'marketing', 'software', 'insurance']
    .reduce((s, k) => s + (Number(pnlData[k]) || 0), 0);
  const netProfit = revenue - totalExpenses;
  const profitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

  if (revenue > 0) {
    if (profitMargin < 5) {
      tips.push({
        type: 'critical',
        title: `${profitMargin.toFixed(1)}% profit margin is dangerously thin`,
        why: 'Net profit margin is what\'s left after ALL expenses. The average restaurant runs 3-9% net profit. Under 5% means one bad month (equipment breaks, slow season, food price spike) can put you in the red. You need a buffer.',
        detail: 'This isn\'t a hustle problem — it\'s a math problem. Small changes to pricing and costs compound into big differences.',
        actions: [
          'Start with your top 5 sellers — a $1 price increase across 5 items at 20 orders/day = $3,000/month more',
          'Review labor scheduling: are you overstaffed during slow hours (2-5 PM)?',
          'Cut any expense you haven\'t actively used in 30 days — subscriptions, services, memberships',
        ],
        source: 'National Restaurant Association Financial Benchmarks',
        learnMore: 'https://restaurant.org/research-and-media/research/research-reports/national-statistics/',
      });
    } else if (profitMargin >= 10) {
      tips.push({
        type: 'success',
        title: `${profitMargin.toFixed(1)}% profit margin — strong position`,
        why: 'A net margin above 10% puts you in the top tier of restaurant profitability. Most full-service restaurants run 3-9%. Protect this — it\'s what lets you reinvest, survive slow months, and eventually grow.',
        detail: 'You\'re doing well. The focus now is protecting this margin and building for the long run.',
        actions: [
          'Re-invest in what drives repeat business: loyalty programs, consistent quality, staff training',
          'Build a cash reserve equal to 2-3 months of fixed costs — your safety net',
        ],
        source: 'Restaurant365 Profitability Benchmarks',
        learnMore: 'https://www.restaurant365.com/blog/average-restaurant-profit-margin/',
      });
    }
  }

  // Always show: best practices with sources
  tips.push({
    type: 'bestpractice',
    title: 'The Consistency Rule',
    why: 'According to a Deloitte study, 44% of restaurant guests say consistent quality is the #1 reason they return. Not price. Not speed. Consistency. Your regulars come back because they trust what they\'re getting. Break that trust and they disappear — quietly.',
    detail: 'One oversized pizza today and a small one tomorrow kills trust faster than a bad review. Consistency IS your brand.',
    actions: [
      'Use portion scales and measuring cups for every build — no eyeballing, ever',
      'Write every recipe down with exact amounts. Train every cook the exact same way.',
      'Check 3 random orders per shift against recipe specs — catch drift before it becomes habit',
      'The goal: a customer should get the exact same product whether you made it or your newest hire did',
    ],
    source: 'Deloitte 2023 Restaurant Consumer Study',
    learnMore: 'https://www2.deloitte.com/us/en/pages/consumer-business/articles/restaurant-industry-outlook.html',
  });

  tips.push({
    type: 'bestpractice',
    title: 'Know Your Numbers Weekly',
    why: 'The restaurants that survive aren\'t always the best cooks — they\'re the best operators. A Cornell University study found that restaurants tracking weekly food costs had 6-8% higher profit margins than those checking monthly. Weekly reviews catch problems before they become habits.',
    detail: 'This is the habit that separates restaurants that last 10 years from restaurants that close in 2.',
    actions: [
      'Pick one day a week (Monday morning works) to review last week\'s food cost, labor cost, and daily sales',
      'Track waste: if you\'re throwing away food, you\'re throwing away money. Keep a waste log by the trash can.',
      'Compare this week vs. last week — trends matter more than single data points',
      'Share the numbers with your team. When they know the score, they play differently.',
    ],
    source: 'Cornell University School of Hotel Administration',
    learnMore: 'https://sha.cornell.edu/research-and-centers/centers-and-institutes/center-for-hospitality-research/',
  });

  tips.push({
    type: 'bestpractice',
    title: 'Menu Engineering: Work Smarter, Not Harder',
    why: 'Menu engineering is a proven technique from Cornell University that categorizes every menu item into Stars (high profit, high popularity), Plowhorses (low profit, high popularity), Puzzles (high profit, low popularity), and Dogs (low both). By knowing which category each item falls into, you make smarter pricing and promotion decisions.',
    detail: 'Your menu layout directly impacts what people order. Items in the top-right corner and first in a section sell 15-20% more. Use this.',
    actions: [
      'Stars: Feature them prominently. These make you money — push them.',
      'Plowhorses: They sell well but don\'t make much. Raise the price slightly or reduce portion cost.',
      'Puzzles: High profit but low sales. Better description, staff recommendation, or repositioning on the menu.',
      'Dogs: Low profit, low sales. Consider removing them — they take up prep time and menu space.',
    ],
    source: 'Kasavana & Smith, Menu Engineering Model (Michigan State University)',
    learnMore: 'https://pos.toasttab.com/blog/on-the-line/menu-engineering',
  });

  return tips;
}
